import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../../../utils/api';

const WS_BASE_URL = process.env.REACT_APP_WS_URL || '';
const WS_RECONNECT_TIMEOUT_MS = 5000;

export function useWebSocketConnection() {
    const [isConnected, setIsConnected] = useState(false);
    const socketRef = useRef(null);
    const onMessageRef = useRef(null);
    const wsResultCallbacksRef = useRef({});
    const unmountedRef = useRef(false);

    const setOnMessage = useCallback((handler) => {
        onMessageRef.current = handler;
    }, []);

    // job_id ベースで WS 結果を待つ。タイムアウト時は null を返す
    const waitForResult = useCallback((jobId, timeoutMs = 60000) => {
        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                delete wsResultCallbacksRef.current[jobId];
                resolve(null);
            }, timeoutMs);
            wsResultCallbacksRef.current[jobId] = (msg) => {
                clearTimeout(timer);
                resolve(msg);
            };
        });
    }, []);

    // WS イベントハンドラをセットアップする内部ヘルパー
    // onConnected は接続成功/失敗時に一度だけ呼ばれる（reconnect 用）
    const setupWs = useCallback((ws, onConnected) => {
        ws.onopen = () => {
            if (!unmountedRef.current) setIsConnected(true);
            onConnected?.(true);
        };
        ws.onclose = () => {
            if (!unmountedRef.current) setIsConnected(false);
            socketRef.current = null;
            Object.values(wsResultCallbacksRef.current).forEach(cb => cb(null));
            wsResultCallbacksRef.current = {};
        };
        ws.onerror = () => {
            if (!unmountedRef.current) setIsConnected(false);
            onConnected?.(false);
        };
        ws.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            if (msg.job_id && wsResultCallbacksRef.current[msg.job_id]) {
                wsResultCallbacksRef.current[msg.job_id](msg);
                delete wsResultCallbacksRef.current[msg.job_id];
            }
            onMessageRef.current?.(msg);
        };
    }, []);

    // WS 再接続を試みる。成功したら true、失敗したら false を返す
    const reconnect = useCallback(() => {
        if (!WS_BASE_URL) return Promise.resolve(false);

        // 既に接続済みなら再接続不要
        if (socketRef.current?.readyState === WebSocket.OPEN) return Promise.resolve(true);

        // 既存接続を閉じる（onclose による callbacks 通知はスキップ）
        if (socketRef.current) {
            const old = socketRef.current;
            old.onclose = null;
            old.onerror = null;
            old.onopen = null;
            socketRef.current = null;
            old.close();
            if (!unmountedRef.current) setIsConnected(false);
        }

        return new Promise((resolve) => {
            let settled = false;
            const timer = setTimeout(() => { if (!settled) { settled = true; resolve(false); } }, WS_RECONNECT_TIMEOUT_MS);
            const done = (val) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(val);
            };

            (async () => {
                try {
                    const { data } = await api.get('accounts/ws-token/');
                    if (unmountedRef.current) { done(false); return; }
                    const ws = new WebSocket(`${WS_BASE_URL}?token=${data.token}`);
                    socketRef.current = ws;
                    setupWs(ws, done);
                } catch {
                    done(false);
                }
            })();
        });
    }, [setupWs]);

    useEffect(() => {
        if (!WS_BASE_URL) return;
        unmountedRef.current = false;

        async function connect() {
            try {
                const { data } = await api.get('accounts/ws-token/');
                if (unmountedRef.current) return;
                const ws = new WebSocket(`${WS_BASE_URL}?token=${data.token}`);
                socketRef.current = ws;
                setupWs(ws, null);
            } catch {
                // ws-token 取得失敗した場合は接続しない（チャット機能は通常通り動作）
            }
        }

        connect();

        return () => {
            unmountedRef.current = true;
            socketRef.current?.close();
            socketRef.current = null;
        };
    }, [setupWs]);

    return { isConnected, socketRef, setOnMessage, waitForResult, reconnect };
}
